/** Shared inbox list/detail helpers — unread counts, IDs, message bubble styling. */
import { cn } from "@/lib/utils";

export function sameConversationId(a, b) {
  if (a == null || b == null) return false;
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
}

export function decrementInboxUnread(summary, channel) {
  if (!summary?.unread) return summary;
  const normChannel = channel
    ? (String(channel).toLowerCase() === "gmail" ? "email" : String(channel).toLowerCase())
    : null;
  const next = {
    ...summary,
    unread: Math.max(0, (summary.unread || 0) - 1),
  };
  if (normChannel && summary.channels?.[normChannel]?.unread > 0) {
    next.channels = {
      ...summary.channels,
      [normChannel]: {
        ...summary.channels[normChannel],
        unread: Math.max(0, summary.channels[normChannel].unread - 1),
      },
    };
  }
  return next;
}

/**
 * Optimistically clear unread for an open row. Returns whether a change was made
 * so callers can avoid double-decrementing summary counts.
 */
export function clearConversationUnreadState(conversations, summary, conversationId, channel) {
  const { conversations: nextList, openRow } = markConversationReadInList(
    conversations,
    conversationId,
  );
  if (!openRow) {
    return { conversations: nextList, summary, cleared: false };
  }
  return {
    conversations: nextList,
    summary: decrementInboxUnread(summary, channel || openRow.channel),
    cleared: true,
  };
}

export function markConversationReadInList(conversations, conversationId) {
  const openRow = conversations.find((c) => sameConversationId(c.id, conversationId));
  if (!openRow?.is_unread) {
    return { conversations, openRow: null };
  }
  return {
    conversations: conversations.map((c) => (
      sameConversationId(c.id, conversationId) ? { ...c, is_unread: false } : c
    )),
    openRow,
  };
}

/**
 * While a thread is open, keep its list row from showing a red unread dot and
 * keep inbox_summary unread counts aligned. Read state is independent of drafts.
 */
export function applyOpenConversationUnreadClear(
  incoming,
  summary,
  conversationId,
  { forceClear = false } = {},
) {
  if (!forceClear || !conversationId) {
    return { conversations: incoming || [], summary };
  }
  const before = (incoming || []).find((c) => sameConversationId(c.id, conversationId));
  const { conversations } = markConversationReadInList(incoming || [], conversationId);
  const nextSummary = before?.is_unread
    ? decrementInboxUnread(summary, before.channel)
    : summary;
  return { conversations, summary: nextSummary };
}

export function isEmailInboxChannel(channel) {
  const ch = (channel || "").toLowerCase();
  return ch === "email" || ch === "gmail";
}

/** True for inbound Gmail/email messages (legacy helper). */
export function isInboundEmailMessage(msg) {
  if (!msg || msg.direction !== "inbound") return false;
  return isEmailInboxChannel(msg.channel);
}

/**
 * True for any Gmail/email message in the thread (inbound or outbound).
 * Outbound email must use the same Gmail-style renderer as inbound — not chat bubbles.
 */
export function isEmailChannelMessage(msg, conversationChannel = null) {
  if (!msg) return false;
  return Boolean(
    isEmailInboxChannel(msg.channel)
    || isEmailInboxChannel(conversationChannel)
    || msg.metadata?.body_html
  );
}

export function threadMessageRowClass(isInbound) {
  return cn(
    "flex w-full min-w-0 px-4 py-1.5",
    isInbound ? "justify-start" : "justify-end",
  );
}

/** Content-sized chat bubbles (WhatsApp/Telegram). */
export const THREAD_BUBBLE_WIDTH_CLASS = "w-fit max-w-[min(85%,42rem)]";

/** Inbound email — wide reading column with a right gutter (left-aligned). */
export const THREAD_INBOUND_EMAIL_WIDTH_CLASS = "w-full max-w-[min(88%,48rem)]";

/** Outbound email — content-sized bubble, right-aligned. */
export const THREAD_OUTBOUND_EMAIL_WIDTH_CLASS = "w-fit max-w-[min(85%,42rem)]";

/** Rich outbound HTML email (iframe) up to the same cap. */
export const THREAD_OUTBOUND_EMAIL_RICH_WIDTH_CLASS = "w-full max-w-[min(85%,42rem)]";

export function messageBubbleClasses({ isInbound, isDraft, isFailed, isSending }) {
  if (isInbound) {
    return "bg-slate-100 text-slate-800 rounded-bl-sm";
  }
  if (isDraft) {
    return "bg-indigo-50 text-slate-900 rounded-br-sm ring-1 ring-indigo-200 ring-dashed";
  }
  if (isFailed) {
    return "bg-red-50 text-red-900 rounded-br-sm ring-1 ring-red-200";
  }
  if (isSending) {
    return "bg-indigo-100 text-slate-900 rounded-br-sm ring-1 ring-indigo-200";
  }
  return "bg-indigo-600 text-white rounded-br-sm";
}

export function messageMetaClasses({ isInbound, isDraft, isFailed, isSending }) {
  if (isInbound) return "text-slate-500";
  if (isDraft) return "text-indigo-700";
  if (isFailed) return "text-red-600";
  if (isSending) return "text-indigo-700";
  return "text-indigo-100";
}

export function deliveryErrorDescription(code) {
  if (code === "outbound_disabled") {
    return "Set SELLIX_LIVE_OUTBOUND=true on the backend and restart the API, then use Retry Send.";
  }
  if (code === "delivery_failed" || code === "delivery_unavailable") {
    return "Check your channel connection in Settings → Connections and try again.";
  }
  if (code === "channel_disconnected") {
    return "Reconnect the channel under Settings → Connections, then retry.";
  }
  if (code === "missing_recipient") {
    return "Refresh the inbox or reconnect the channel, then try again.";
  }
  if (code === "not_retryable") {
    return "Only failed messages can be retried.";
  }
  return undefined;
}

export function gmailDeliveryErrorDescription(code) {
  if (code === "delivery_failed") {
    return "Check Gmail connection in Settings → Connections and try again.";
  }
  if (code === "missing_recipient") {
    return "Refresh the inbox or reconnect Gmail, then try again.";
  }
  return undefined;
}

/** One-shot deep-link flag used by Today Pulse → Drafts pending. */
export function stripInboxSelectParam(searchParams) {
  const next = new URLSearchParams(searchParams);
  next.delete("select");
  return next;
}

/**
 * Decide how to handle `?select=first` without affecting normal inbox navigation.
 * @returns {"none"|"wait"|"scrub"|"open"}
 */
export function resolveInboxSelectFirst({ selectFirst, conversationId, listLoading, list }) {
  if (!selectFirst) return { action: "none" };
  if (conversationId) return { action: "scrub" };
  if (listLoading) return { action: "wait" };
  if (!list?.length) return { action: "scrub" };
  return { action: "open", id: list[0].id };
}

const DRAFT_GEN_POLL_MS = 1500;
/** Align with backend compose timeout (~240s per-attempt, ~510s outer). */
const DRAFT_GEN_TIMEOUT_MS = 330_000;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function conversationHasPendingDraft(conversationsApi, conversationId) {
  const detail = await conversationsApi.get(conversationId, {
    background: true,
    markRead: false,
  });
  return (detail?.messages || []).some((m) => m.status === "draft");
}

async function _checkDraftStatus(api, id) {
  try {
    return await api.draftGenerationStatus(id, { background: true });
  } catch {
    return null;
  }
}

function _checkFailedStatus(status) {
  if (status?.status !== "failed") return;
  const err = new Error(
    status.error
      || "Unable to generate an AI draft at the moment. Please try again in a few moments.",
  );
  err.code = "draft_generation_failed";
  throw err;
}

async function _checkThreadIfNeeded(conversationsApi, conversationId, status, ticks, kickoff) {
  // Multi-draft regenerate/ensure creates drafts one-by-one. Do not treat the
  // first pending draft as completion while the job is still generating.
  if (status?.status === "generating") {
    return null;
  }

  // Periodic thread check for cross-worker durability when status is idle/missing.
  if (ticks % 3 !== 0) return null;

  const hasPending = await conversationHasPendingDraft(conversationsApi, conversationId).catch(() => false);
  if (hasPending) {
    return { status: "ready", message_id: kickoff?.message_id || status?.message_id || null };
  }
  return null;
}

async function _resolveIdleOrMissingJob(conversationsApi, conversationId, kickoff, ticks) {
  const hasPending = await conversationHasPendingDraft(conversationsApi, conversationId).catch(() => false);
  if (hasPending) {
    return { status: "ready", message_id: kickoff?.message_id || null };
  }
  // Job claim released (ensure skipped) or never existed — stop the spinner.
  // Require a few idle polls before settling. Do not short-circuit on kickoff
  // status: in-process job state is per API worker, so a single "idle" can mean
  // we hit a different worker while compose is still running elsewhere.
  if (ticks >= 2) {
    return { status: "idle", message_id: null };
  }
  return null;
}

/**
 * Wait until Ask-AI-to-draft finishes.
 *
 * Prefer the lightweight generation-status endpoint while a job is running.
 * Fall back to a full thread GET periodically for cross-worker durability
 * (in-process job state is not shared across API workers).
 */
function _checkKickoffReady(kickoff) {
  if (!kickoff?.message_id) return null;
  if (kickoff.status === "draft" || kickoff.status === "ready") {
    return { status: "ready", message_id: kickoff.message_id };
  }
  return null;
}

async function _pollDraftIteration(conversationsApi, conversationId, kickoff, ticks) {
  const status = await _checkDraftStatus(conversationsApi, conversationId);
  if (status?.status === "ready") {
    return { status: "ready", message_id: status.message_id || null };
  }
  _checkFailedStatus(status);

  if (!status || status.status === "idle") {
    const idleResult = await _resolveIdleOrMissingJob(
      conversationsApi, conversationId, kickoff, ticks,
    );
    if (idleResult) return idleResult;
  }

  return await _checkThreadIfNeeded(
    conversationsApi, conversationId, status, ticks, kickoff,
  );
}

async function _handleDraftTimeout(conversationsApi, conversationId, kickoff) {
  const hasPending = await conversationHasPendingDraft(conversationsApi, conversationId).catch(() => false);
  if (hasPending) {
    return { status: "ready", message_id: kickoff?.message_id || null };
  }
  const err = new Error(
    "Draft generation is taking longer than expected. Refresh the thread in a moment.",
  );
  err.code = "draft_generation_timeout";
  throw err;
}

export async function waitForDraftGeneration(conversationsApi, conversationId, kickoff = null) {
  const kickoffReady = _checkKickoffReady(kickoff);
  if (kickoffReady) return kickoffReady;

  const started = Date.now();
  let ticks = 0;

  while (Date.now() - started < DRAFT_GEN_TIMEOUT_MS) {
    ticks += 1;
    const stepResult = await _pollDraftIteration(conversationsApi, conversationId, kickoff, ticks);
    if (stepResult) return stepResult;
    await sleep(DRAFT_GEN_POLL_MS);
  }

  return await _handleDraftTimeout(conversationsApi, conversationId, kickoff);
}

